import type { OrderModel } from 'src/types/order';
import type { UseFormReturn } from 'react-hook-form';

import { useState } from 'react';
import { useNavigate } from 'react-router';

import { paths } from 'src/routes/paths';

import { usePendingPaymentsCount } from 'src/hooks/use-pending-payments-count';
import { useUpdateExternalOrder } from 'src/hooks/orders/useUpdateExternalOrder';

import { createExternalOrder } from 'src/api/order.api';

import { toast } from 'src/components/snackbar';

import { useAuthContext } from 'src/auth/hooks';

import {
  buildUpdatePayload,
  buildCreatePayload,
  mapPaymentMethodToBackend,
} from '../utils/order-mappers';

import type {
  OrderItem,
  PaymentMethodEnum,
  BillingSchemaType,
  NewOrderSchemaType,
} from '../types/order-form.types';

interface UseOrderSubmitParams {
  isEditMode: boolean;
  currentOrder?: OrderModel;
  orderItems: OrderItem[];
  billingMethod: 'email' | 'platform';
  paymentMethod: PaymentMethodEnum;
  methods: UseFormReturn<NewOrderSchemaType>;
  validateBillingForm: () => Promise<boolean>;
  billingMethods: UseFormReturn<BillingSchemaType>;
}

export function useOrderSubmit({
  isEditMode,
  currentOrder,
  orderItems,
  billingMethod,
  paymentMethod,
  methods,
  validateBillingForm,
  billingMethods,
}: UseOrderSubmitParams) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { updateExternalOrder, isLoading: isUpdating } = useUpdateExternalOrder();
  const { refresh: refreshPendingPaymentsCount } = usePendingPaymentsCount();
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const isProcessing = isCreatingOrder || isUpdating;

  const handleSubmit = async () => {
    // Guarda de re-entrada: evita doble-submit (doble click, Enter tras click, submit
    // duplicado del panel de facturacion) que disparaba dos requests de creacion en paralelo.
    // No depender solo del `disabled` del boton visible, que no cubre otros triggers de submit.
    if (isCreatingOrder || isUpdating) {
      return;
    }

    try {
      if (!isEditMode && orderItems.length === 0) {
        toast.error('Añade al menos un medio al pedido antes de crearlo');
        return;
      }

      setIsCreatingOrder(true);

      const isValid = await validateBillingForm();
      if (!isValid) {
        toast.error('Es necesario rellenar los datos de facturación');
        setIsCreatingOrder(false);
        return;
      }

      const billingData = billingMethods.getValues();

      if (isEditMode && currentOrder) {
        await handleUpdate(currentOrder, billingData);
      } else {
        await handleCreate(billingData);
      }
    } catch (error: any) {
      console.error('Error al procesar el pedido:', error);
      const errorMessage = extractErrorMessage(error, isEditMode);
      toast.error(errorMessage);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleUpdate = async (order: OrderModel, billingData: any) => {
    const formData = methods.getValues();
    const updatedOrderData = await buildUpdatePayload(order, formData, billingData, billingMethod);

    await updateExternalOrder(order.id!, updatedOrderData);
    toast.success('Pedido actualizado correctamente');
    navigate(paths.dashboard.order.externals.root);
  };

  const handleCreate = async (billingData: any) => {
    const backendPaymentMethod = mapPaymentMethodToBackend(paymentMethod);
    const isPendingPayment = backendPaymentMethod === 'Pending';

    const requestData = buildCreatePayload(
      orderItems,
      billingData,
      billingMethod,
      backendPaymentMethod,
      user?.id
    );

    await createExternalOrder(requestData);

    if (isPendingPayment) {
      try {
        await refreshPendingPaymentsCount();
      } catch (refreshError) {
        console.error('Error al actualizar contador:', refreshError);
      }
    }

    toast.success('Pedido creado correctamente');
    navigate(paths.dashboard.order.externals.root);
  };

  return {
    handleSubmit,
    isProcessing,
  };
}

function extractErrorMessage(error: any, isEditMode: boolean): string {
  let errorMessage = isEditMode
    ? 'Error al actualizar el pedido. Por favor, intentelo de nuevo.'
    : 'Error al crear el pedido. Por favor, intentelo de nuevo.';

  if (error?.response?.data) {
    const errorData = error.response.data;

    if (errorData.errors) {
      const errorMessages = Object.entries(errorData.errors)
        .map(([field, messages]: [string, any]) => {
          const fieldErrors = Array.isArray(messages) ? messages : [messages];
          return `${field}: ${fieldErrors.join(', ')}`;
        })
        .join('\n');
      errorMessage = `Errores de validacion:\n${errorMessages}`;
    } else if (errorData.message) {
      errorMessage = errorData.message;
    } else if (errorData.title) {
      errorMessage = errorData.title;
    }
  } else if (error?.message) {
    errorMessage = error.message;
  }

  return errorMessage;
}
